import { abschnitt, ersetze, h, knopf, meldung, schritte, setzeMeldung } from '../dom.js';
import { melde } from '../ergebnisse.js';
import { istIos } from '../geraet.js';
import type { Ansicht } from '../router.js';
import { lies, schreibe } from '../speicher.js';
import { formatiereZeitpunkt } from '../zeit.js';
import { Aufraeumer, kopf, pruefpunktStand } from './gemeinsam.js';

const DAUER_SEK = 120;

interface Lauf {
  mitWakeLock: boolean;
  beginn: number;
  timer: number;
  sperre?: WakeLockSentinel;
  verstecktAm?: number;
  freigegebenNachSek?: number;
  beendet: boolean;
}

/**
 * „Bildschirm bleibt an“ im Durchlauf (Konzept, Bedienung vor Ort). Der Test
 * wertet selbst aus: Geht der Bildschirm aus, wird die Seite unsichtbar.
 * Berührungen machen den Test ungültig, weil sie die Sperre ohnehin aufschieben.
 */
export const bildschirm: Ansicht = async (ziel) => {
  const aufraeumer = new Aufraeumer();
  const anzeige = h('p', { class: 'countdown', 'aria-live': 'off' }, '');
  const status = meldung();
  const gegenprobeText = h('p', {});
  let lauf: Lauf | undefined;

  const zeigeGegenprobe = async () => {
    const g = await lies('gegenprobe');
    gegenprobeText.textContent = g
      ? g.sekunden === null
        ? `Gegenprobe ${formatiereZeitpunkt(g.zeit)}: Bildschirm blieb ${DAUER_SEK / 60}\u00a0Minuten an. Die automatische Sperre steht wohl länger oder auf „Nie“.`
        : `Gegenprobe ${formatiereZeitpunkt(g.zeit)}: Bildschirm ging nach ${g.sekunden}\u00a0s aus. Die automatische Sperre greift.`
      : 'Noch keine Gegenprobe.';
  };

  const beende = async (ergebnis: 'durch' | 'aus' | 'beruehrt' | 'abgebrochen') => {
    if (!lauf || lauf.beendet) return;
    const l = lauf;
    l.beendet = true;
    window.clearInterval(l.timer);
    await l.sperre?.release().catch(() => {});
    lauf = undefined;
    const sekunden = Math.round(((l.verstecktAm ?? Date.now()) - l.beginn) / 1000);
    ersetze(knopfReihe, startKnopf, gegenprobeKnopf);
    anzeige.textContent = '';

    if (ergebnis === 'beruehrt' || ergebnis === 'abgebrochen') {
      setzeMeldung(status, ergebnis === 'beruehrt' ? 'Bildschirm wurde berührt, Test ungültig. Bitte erneut starten und das Handy nicht berühren.' : 'Test abgebrochen.', 'info');
      return;
    }
    if (!l.mitWakeLock) {
      await schreibe('gegenprobe', { zeit: new Date().toISOString(), sekunden: ergebnis === 'aus' ? sekunden : null });
      await zeigeGegenprobe();
      setzeMeldung(status, 'Gegenprobe gespeichert.', 'ok');
      return;
    }
    const gegenprobe = (await lies('gegenprobe')) ?? null;
    if (ergebnis === 'durch') {
      await melde('bildschirm-an', 'ok', `Bildschirm blieb ${DAUER_SEK / 60}\u00a0Minuten an (Wake Lock).`, {
        dauerSek: DAUER_SEK,
        wakeLockVomSystemBeendetNachSek: l.freigegebenNachSek ?? null,
        gegenprobe,
      });
      setzeMeldung(status, 'Bestanden: Der Bildschirm blieb an.', 'ok');
    } else {
      await melde('bildschirm-an', 'fehler', `Bildschirm ging trotz Wake Lock nach ${sekunden}\u00a0s aus (oder die App wurde verlassen).`, {
        nachSek: sekunden,
        wakeLockVomSystemBeendetNachSek: l.freigegebenNachSek ?? null,
        gegenprobe,
      });
      setzeMeldung(status, `Nicht bestanden: nach ${sekunden}\u00a0s ausgegangen.`, 'fehler');
    }
  };

  const starte = async (mitWakeLock: boolean) => {
    if (lauf) return;
    let sperre: WakeLockSentinel | undefined;
    if (mitWakeLock) {
      if (!('wakeLock' in navigator)) {
        await melde('bildschirm-an', 'nicht_unterstuetzt', 'Der Browser kennt keinen Wake Lock (navigator.wakeLock fehlt).');
        setzeMeldung(status, 'Wake Lock wird nicht unterstützt.', 'fehler');
        return;
      }
      try {
        sperre = await navigator.wakeLock.request('screen');
      } catch (fehler) {
        await melde('bildschirm-an', 'fehler', `Wake Lock abgelehnt: ${(fehler as Error).name}: ${(fehler as Error).message}`);
        setzeMeldung(status, 'Wake Lock wurde abgelehnt.', 'fehler');
        return;
      }
    }
    const beginn = Date.now();
    const neu: Lauf = { mitWakeLock, beginn, beendet: false, timer: 0, ...(sperre ? { sperre } : {}) };
    sperre?.addEventListener('release', () => {
      if (!neu.beendet && neu.freigegebenNachSek === undefined) neu.freigegebenNachSek = Math.round((Date.now() - beginn) / 1000);
    });
    neu.timer = window.setInterval(() => {
      const rest = DAUER_SEK - Math.floor((Date.now() - beginn) / 1000);
      // War die Seite zwischendurch unsichtbar, entscheidet beiSichtbarkeit (ausgegangen), nie der Zeitablauf.
      if (neu.verstecktAm !== undefined) return;
      if (rest <= 0 && document.visibilityState === 'visible') void beende('durch');
      else anzeige.textContent = `${Math.floor(Math.max(rest, 0) / 60)}:${String(Math.max(rest, 0) % 60).padStart(2, '0')}`;
    }, 250);
    lauf = neu;
    ersetze(knopfReihe, knopf('Abbrechen', () => beende('abgebrochen'), 'sekundaer'));
    setzeMeldung(status, mitWakeLock ? 'Test läuft. Handy hinlegen und nicht berühren.' : 'Gegenprobe läuft ohne Wake Lock. Handy hinlegen und nicht berühren.');
  };

  const beiSichtbarkeit = async () => {
    if (!lauf) return;
    if (document.visibilityState === 'hidden') {
      lauf.verstecktAm ??= Date.now();
    } else if (lauf.verstecktAm !== undefined) {
      await beende('aus');
    } else if (lauf.mitWakeLock && lauf.sperre?.released) {
      // Vom System beendet, Seite aber sichtbar: wie die echte App neu anfordern.
      lauf.sperre = await navigator.wakeLock.request('screen').catch(() => undefined);
    }
  };
  const beiBeruehrung = (e: Event) => {
    if (lauf && !(e.target as Element | null)?.closest('button')) void beende('beruehrt');
  };
  aufraeumer.hoere(document, 'visibilitychange', () => void beiSichtbarkeit());
  aufraeumer.hoere(document, 'pointerdown', beiBeruehrung);
  aufraeumer.dazu(() => void beende('abgebrochen'));

  const startKnopf = knopf(`Test starten (${DAUER_SEK / 60}\u00a0Minuten)`, () => starte(true));
  const gegenprobeKnopf = knopf('Gegenprobe ohne Wake Lock', () => starte(false), 'sekundaer');
  const knopfReihe = h('div', { class: 'knopfreihe' }, startKnopf, gegenprobeKnopf);

  ziel.append(
    ...kopf('Bildschirm bleibt an', 'Im Durchlauf darf der Bildschirm nicht nach 30 Sekunden ausgehen, denn mit Handschuhen ist Entsperren mühsam.'),
    abschnitt(
      'Vorbereitung',
      schritte(
        istIos()
          ? 'Automatische Sperre auf 30\u00a0Sekunden stellen: Einstellungen → Anzeige & Helligkeit → Automatische Sperre.'
          : 'Bildschirm-Timeout auf 30\u00a0Sekunden stellen: Einstellungen → Display → Bildschirm-Timeout.',
        'Optional zuerst die Gegenprobe ohne Wake Lock: Der Bildschirm muss dann ausgehen.',
        `Test starten, Handy hinlegen und ${DAUER_SEK / 60}\u00a0Minuten nicht berühren.`,
        'Geht der Bildschirm aus, nach dem Entsperren hierher zurückkehren: Die App wertet selbst aus.',
      ),
    ),
    abschnitt('Test', pruefpunktStand('bildschirm-an', aufraeumer), anzeige, knopfReihe, status, gegenprobeText),
  );
  await zeigeGegenprobe();
  return aufraeumer.alles;
};
